import { DocumentState } from '@app/core/enums/document/document-state.enum';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { IRelatedAsset, RelatedAsset } from '@app/core/model/entities/asset/asset';
import { AssetsRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { Entity } from '@app/core/model/entities/entity';
import { getValue, hasProperty, setValue } from '@app/shared/extra/utils';
import { Expose, Transform } from 'class-transformer';
import { basename, extname } from 'path-browserify';

export class Document extends Entity implements AssetsRelatedEntity {
  public objectType = EntityTypeEnum.DOCUMENT;

  public entityId: string;
  public path: string;
  public size: number;
  public mimeType: string;

  @Transform(fn => EntityTypeEnum[fn.value])
  public entityType: EntityTypeEnum;

  @Transform(fn => DocumentType[fn.value])
  public type: DocumentType;

  @Transform(fn => DocumentState[fn.value])
  public state: DocumentState;

  @Expose()
  public toString(): string {
    return this.name;
  }

  public getNameWithoutExtension(): string {
    return basename(this.name, this.getExtension());
  }

  public getExtension(): string {
    return extname(this.name);
  }

  /**
   * Get all related assets inside the Document computed properties.
   * @return List of related assets
   */
  public get relatedAssets(): RelatedAsset[] {
    if (!this.computedProperties?.['assets']) return [];
    return this.computedProperties?.['assets'].map((asset: IRelatedAsset) => new RelatedAsset(asset));

  }

  /**
   * Whether the file's name is valid.
   * @param fileName File's name.
   * @return True if the name is valid, false otherwise.
   */
  public static validFileName(fileName: string): boolean {
    return /^[a-zA-Z0-9À-ÖØ-öø-ÿ\s.\-_()']*$/.test(fileName);
  }

  // Properties to be renamed
  protected static inputMapping = {
    assets: {
      input: ['computedProperties', 'assets', 'id'],
      output: 'entityId'
    }
  };

  /**
   * Some GraphQL inputs require different keys than those available in the Form State Service
   * since those keys correspond to field codes, so we need to replace them accordingly
   * @param obj the Graphql input object
   */
  public static transformPropertiesForInput(obj: Record<string, unknown>): void {
    // Transform input, mapping keys as defined in the inputMapping above
    for (const inputMappingElement in Document.inputMapping) {
      const inputMapping = Document.inputMapping[inputMappingElement];
      if (hasProperty(obj, inputMapping.input)) {
        const source = getValue(obj, inputMapping.input);
        if (inputMapping.transform) {
          setValue(obj, [inputMapping.output], inputMapping.transform(source));
        } else {
          setValue(obj, [inputMapping.output], source);
        }
      }
    }

    // Clean up by removing older keys
    for (const inputMappingElement in Document.inputMapping) {
      delete obj[Document.inputMapping[inputMappingElement].input.first()];
    }
  }
}

export class PresignedUrl {
  public fileName: string;
  public documentName: string;
  public url: string;
}

export class DocumentToCreateFromFile {
  constructor(fileName: string, documentName: string) {
    this.fileName = fileName;
    this.document = new Document();
    this.document.name = documentName;
  }

  public fileName: string;
  public document: Document;
}

export interface CreateDocumentInput {
  name: string;
  fileName: string;
  type: DocumentType;
  state: DocumentState;
  properties?: Record<string, any>;
  entityId: string;
  entityType: EntityTypeEnum;
}

export interface UpdateDocumentInput {
  name?: string;
  type?: string;
  state?: string;
  entityId?: string;
  entityType?: EntityTypeEnum;
  properties?: Record<string, any>;
}
